import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {message, Tooltip} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {CheckboxController, CreditCardPreview} from "../PaymentInputHelper.jsx";
import {PaymentType} from "../../../../consts.js";
import {Images} from "../../../../images/Images.jsx";
import {HttpClient} from "../../../../http/HttpClient.jsx";
import {EventBus} from "../../../../bus/EventBus.jsx";
import {Payment} from "../Payment.jsx";
import {isEmpty} from "lodash";
import {AreYouSureModal} from "../../../../components/index.jsx";
import "../payment.css";

export const CreditCardPaymentInput = ({
    paymentType,
    setPaymentType,
    creditCard,
    me,
    company,
    setCreditCard,
    creditCardToolTipInfo,
    shouldSaveCreditCard = true
}) => {
    const [removeCardModalVisibility, setRemoveCardModalVisibility] = useState(false);
    const creditCardKey = useRef(0);

    const removeSavedCard = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        setRemoveCardModalVisibility(true);
    }, []);

    const onYesToRemoveSavedCard = useCallback(async () => {
        setRemoveCardModalVisibility(null);

        try {
            await HttpClient.post(`/api/users/${me?.userId}/removeCreditCard`);
            setCreditCard({});
            message.success("הכרטיס נמחק בהצלחה!", 5);
        } catch (e) {
            EventBus.triggerError(
                "server-error",
                {content: {description: "Unfortunately we couldn't complete your request :("}},
                e.message
            );
        }
    }, [me?.userId, setCreditCard]);

    const onCardSavedSuccess = useCallback(
        creditCardData => {
            setCreditCard({
                token: creditCardData.token,
                expDate: creditCardData.expiryMonth + creditCardData.expiryYear,
                last4Digits: creditCardData.last4Digits
            });

            if (shouldSaveCreditCard) {
                Promise.resolve().then(async () => {
                    try {
                        await HttpClient.post(`/api/users/${me.userId}/updateCreditCard`, {
                            creditCardData
                        });
                        message.success("!הכרטיס נשמר בהצלחה", 5);
                    } catch (e) {
                        message.error("Something went wrong, please try again later.", 5);
                    }
                });
            }
        },
        [me?.userId, setCreditCard, shouldSaveCreditCard]
    );

    const parseResponse = useCallback(
        (response, key) => {
            if (key !== creditCardKey.current) {
                return;
            }

            if (response.error) {
                EventBus.triggerError("server-error", {
                    content: {description: ["Unfortunately we didn't manage to save your card :(", response.error]}
                });
            } else {
                creditCardKey.current++;
                onCardSavedSuccess(response);
            }
        },
        [creditCardKey.current]
    );

    const creditCardPaymentComponent = useMemo(() => {
        const key = creditCardKey.current;
        return (
            <Payment
                key={key}
                creditCardExist={creditCard.last4Digits}
                parseResponse={response => parseResponse(response, key)}
            />
        );
    }, [parseResponse, creditCard]);

    useEffect(() => {
        async function getCreditCard() {
            const creditCardData = await HttpClient.safeGet(`/api/companies/${company.companyId}/getCreditCardData`);

            if (creditCardData?.error) {
                message.error("Error while fetching credit card data");
                return;
            }

            if (!isEmpty(creditCardData)) {
                setCreditCard({
                    token: creditCardData.token,
                    expDate: creditCardData.expDate,
                    last4Digits: creditCardData.last4Digits
                });
            }
        }

        if (company && shouldSaveCreditCard) {
            getCreditCard();
        }
    }, [company, setCreditCard]);

    return (
        <>
            <AreYouSureModal
                visible={!!removeCardModalVisibility}
                onClose={() => setRemoveCardModalVisibility(null)}
                title="בטוח שאתה רוצה למחוק את פרטי האשראי?"
                saveStyle={{color: "white", backgroundColor: "#1260D1"}}
                onYes={onYesToRemoveSavedCard}
                onNo={() => setRemoveCardModalVisibility(null)}
            />

            <div className="payment-input-container">
                <div className="payment-input-options-header">
                    <CheckboxController
                        isChecked={paymentType === PaymentType.Card}
                        setPaymentType={type => setPaymentType(type)}
                        type={PaymentType.Card}
                    />
                    כרטיס אשראי
                    <Tooltip title={creditCardToolTipInfo}>
                        <QuestionCircleOutlined className="payment-input-tooltip-icon" />
                    </Tooltip>
                </div>
                <CreditCardPreview last4Digits={creditCard.last4Digits} removeSavedCard={removeSavedCard} />
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start"
                    }}>
                    {paymentType === PaymentType.Card ? (
                        <div style={{display: "flex", flexDirection: "column"}}>
                            {creditCardPaymentComponent}
                            <img
                                alt="credit-card-image"
                                src={Images.creditCardsInARow}
                                style={{width: "200px", marginTop: "20px"}}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};
